import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download } from "lucide-react";
import api, { download, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useApi, PageHeader, Loading, Empty, StatusBadge, Select, TableWrap, KpiCard } from "../components/common";
import { useDirectory } from "../components/LeadForm";
import { Button } from "../components/ui/button";
import { fdate, todayIST } from "../lib/format";

export default function Tasks() {
  const { user, meta } = useAuth();
  const owner = user.role === "owner";
  const [flt, setFlt] = useState({ status: "open", assigned_to: "", overdue: false, q: "" });
  const designers = useDirectory("designer");
  const qs = new URLSearchParams(Object.fromEntries(Object.entries(flt).filter(([, v]) => v !== "" && v !== false))).toString();
  const { data, loading, reload } = useApi(`/tasks?${qs}`);
  const t = todayIST();
  const setStatus = async (task, status) => {
    let note = "";
    if (status === "Revision Required") { note = window.prompt("Revision instructions?") || ""; if (!note) return; }
    try { await api.post(`/tasks/${task.id}/status`, { status, note }); toast.success(`Task → ${status}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const statuses = owner ? meta?.task_statuses || [] : (meta?.task_statuses || []).filter((s) => s !== "Revision Required");
  const items = data || [];
  return (
    <div>
      <PageHeader title={owner ? "All Tasks" : "My Tasks"} subtitle="Update status as you work. Completion time is recorded automatically."
        actions={<Button variant="outline" onClick={() => download("/export/tasks", "tasks.csv")} data-testid="task-export-button"><Download className="h-4 w-4 mr-1" />Export</Button>} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KpiCard label="Shown" value={items.length} tone="revenue" />
        <KpiCard label="Overdue" value={items.filter((x) => x.due_date && x.due_date < t && x.status !== "Completed").length} tone="expense" />
        <KpiCard label="In Review" value={items.filter((x) => x.status === "In Review").length} tone="pending" />
        <KpiCard label="Revisions" value={items.reduce((s, x) => s + (x.revision_count || 0), 0)} tone="profit" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <input data-testid="task-filter-search" className="vmw-input" placeholder="Search tasks…" value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} />
        <Select testid="task-filter-status" placeholder="All statuses" options={[{ value: "open", label: "Open (not completed)" }, ...(meta?.task_statuses || [])]} value={flt.status} onChange={(v) => setFlt({ ...flt, status: v })} />
        {owner && <Select testid="task-filter-designer" placeholder="All designers" options={designers.map((d) => ({ value: d.id, label: d.name }))} value={flt.assigned_to} onChange={(v) => setFlt({ ...flt, assigned_to: v })} />}
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="task-filter-overdue" checked={flt.overdue} onChange={(e) => setFlt({ ...flt, overdue: e.target.checked })} />Overdue only</label>
      </div>
      <TableWrap>
        {loading && !data ? <Loading /> : !items.length ? <Empty text="No tasks" /> : (
          <table className="vmw-table" data-testid="tasks-table">
            <thead><tr><th>Task</th><th>Project</th>{owner && <th>Designer</th>}<th>Priority</th><th>Due</th><th>Revisions</th><th>Completed</th><th>Status</th></tr></thead>
            <tbody>{items.map((x) => (
              <tr key={x.id} data-testid={`tasks-row-${x.id}`}>
                <td className="font-medium">{x.name}<div className="font-mono text-[11px] text-muted-foreground">{x.code}</div></td>
                <td><Link to={`/projects/${x.project_id}`} className="hover:text-primary">{x.project_name}</Link></td>
                {owner && <td className="text-xs">{x.assigned_name || "—"}</td>}
                <td><StatusBadge value={x.priority} /></td>
                <td className={x.due_date && x.due_date < t && x.status !== "Completed" ? "text-rose-600 font-semibold" : ""}>{fdate(x.due_date)}</td>
                <td>{x.revision_count || 0}</td><td>{fdate(x.completed_date)}</td>
                <td><select data-testid={`tasks-status-${x.id}`} className="text-xs border rounded-md px-2 py-1" value={x.status} onChange={(e) => setStatus(x, e.target.value)}>
                  {!statuses.includes(x.status) && <option>{x.status}</option>}{statuses.map((s) => <option key={s}>{s}</option>)}</select></td>
              </tr>))}</tbody>
          </table>)}
      </TableWrap>
    </div>
  );
}
